import { api } from '../utils/api';

// ── Neon asset shape ───────────────────────────────────────────────
interface NeonAsset {
  id:              string;
  assetNumber:     string;
  name:            string;
  category:        string;
  make:            string | null;
  model:           string | null;
  year:            number | null;
  serialNumber:    string | null;
  regoNumber:      string | null;
  status:          string;   // IN_YARD | ON_SITE | IN_SERVICE | OUT_OF_SERVICE | DISPOSED
  currentJobId:    string | null;
  regoExpiry:      string | null;
  ctpExpiry:       string | null;
  insuranceExpiry: string | null;
  nextServiceDate: string | null;
  currentOdometer: number | null;
  purchasePrice:   string | null;
  bookValue:       string | null;
  hasOpenDefect:   boolean;
  notes:           string | null;
  createdAt:       string;
}

// Map Neon status enum → UI status enum
function mapStatus(neonStatus: string): string {
  const map: Record<string, string> = {
    IN_YARD:         'AVAILABLE',
    ON_SITE:         'ALLOCATED',
    IN_SERVICE:      'IN_SERVICE',
    DEFECTIVE:       'OUT_OF_SERVICE',
    DISPOSED:        'DISPOSED',
  };
  return map[neonStatus] ?? 'AVAILABLE';
}

// Map Neon asset → UI Equip type
export function mapNeonAsset(a: NeonAsset): any {
  return {
    id:                  a.id,
    assetNumber:         a.assetNumber,
    category:            a.category,
    name:                a.name,
    make:                a.make    ?? '',
    model:               a.model   ?? '',
    year:                a.year    ?? 2020,
    registrationNumber:  a.regoNumber  ?? null,
    serialNumber:        a.serialNumber ?? null,
    status:              mapStatus(a.status),
    currentJobId:        a.currentJobId ?? null,
    currentJobNumber:    null,   // resolved in component if needed
    ownedOrLeased:       'OWNED',
    purchaseCost:        a.purchasePrice ? parseFloat(a.purchasePrice) : 0,
    currentValue:        a.bookValue     ? parseFloat(a.bookValue)     : 0,
    regoExpiry:          a.regoExpiry      ?? null,
    ctpExpiry:           a.ctpExpiry       ?? null,
    insuranceExpiry:     a.insuranceExpiry ?? null,
    nextServiceDate:     a.nextServiceDate ?? null,
    nextServiceKm:       null,
    currentOdometer:     a.currentOdometer ?? null,
    calibrationDue:      null,
    // Defects: filled when hasOpenDefect=true — detail call needed for full list
    defects: a.hasOpenDefect
      ? [{ id: 'open', severity: 'MAJOR', description: 'Open defect — view asset for details', status: 'OPEN', reportedAt: a.createdAt }]
      : [],
    // Extras for display
    notes:    a.notes ?? '',
    _isNeon:  true,
  };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isNeonAsset(id: string): boolean {
  return UUID_RE.test(id);
}

export async function fetchPlantAssets(): Promise<any[]> {
  const { data } = await api.get<{ data: NeonAsset[] } | NeonAsset[]>('/api/plant/assets');
  const raw: NeonAsset[] = Array.isArray(data) ? data : ((data as any).data ?? []);
  return raw.map(mapNeonAsset);
}

export async function createPlantAsset(payload: {
  assetNumber:      string;
  name:             string;
  category:         string;
  make?:            string;
  model?:           string;
  year?:            number;
  registrationNumber?: string;
  purchaseCost?:    number;
  regoExpiry?:      string;
  ctpExpiry?:       string;
  insuranceExpiry?: string;
  nextServiceDate?: string;
  notes?:           string;
}): Promise<void> {
  try {
    await api.post('/api/plant/assets', {
      assetNumber:  payload.assetNumber,
      name:         payload.name,
      category:     payload.category,
      make:         payload.make          ?? null,
      model:        payload.model         ?? null,
      year:         payload.year          ?? null,
      regoNumber:   payload.registrationNumber ?? null,
      purchasePrice: payload.purchaseCost ?? null,
      regoExpiry:   payload.regoExpiry    ?? null,
      ctpExpiry:    payload.ctpExpiry     ?? null,
      insuranceExpiry: payload.insuranceExpiry ?? null,
      nextServiceDate: payload.nextServiceDate ?? null,
      notes:        payload.notes         ?? null,
    });
  } catch (err) {
    console.warn('createPlantAsset to Neon failed:', err);
  }
}

export async function reportDefectToNeon(assetId: string, payload: {
  description: string;
  severity:    string;
}): Promise<void> {
  if (!isNeonAsset(assetId)) return;
  try {
    await api.post('/api/plant/defects', {
      assetId,
      description: payload.description,
      severity:    payload.severity,
    });
  } catch (err) {
    console.warn('reportDefectToNeon failed:', err);
  }
}
